import { useCallback, useEffect, useRef, useState } from 'react'

export interface DraftController<T> {
  value: T
  set: (value: T | ((current: T) => T)) => void
  reset: (value: T) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
  dirty: boolean
  markSaved: (value?: T) => void
}

export function useDraftHistory<T>(initial: T): DraftController<T> {
  const [past, setPast] = useState<T[]>([])
  const [value, setValue] = useState<T>(initial)
  const [future, setFuture] = useState<T[]>([])
  const saved = useRef(JSON.stringify(initial))

  const set = useCallback((next: T | ((current: T) => T)) => {
    setValue((current) => {
      const resolved = typeof next === 'function' ? (next as (current: T) => T)(current) : next
      if (JSON.stringify(resolved) === JSON.stringify(current)) return current
      setPast((items) => [...items.slice(-99), current])
      setFuture([])
      return resolved
    })
  }, [])

  const reset = useCallback((next: T) => {
    setPast([])
    setFuture([])
    setValue(next)
    saved.current = JSON.stringify(next)
  }, [])

  const undo = useCallback(() => {
    setPast((items) => {
      if (!items.length) return items
      const previous = items[items.length - 1]
      setValue((current) => {
        setFuture((next) => [current, ...next].slice(0, 100))
        return previous
      })
      return items.slice(0, -1)
    })
  }, [])

  const redo = useCallback(() => {
    setFuture((items) => {
      if (!items.length) return items
      const next = items[0]
      setValue((current) => {
        setPast((previous) => [...previous.slice(-99), current])
        return next
      })
      return items.slice(1)
    })
  }, [])

  const markSaved = useCallback((persisted?: T) => {
    saved.current = JSON.stringify(persisted ?? value)
  }, [value])

  return {
    value,
    set,
    reset,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    dirty: saved.current !== JSON.stringify(value),
    markSaved,
  }
}

export function useAutosave(
  enabled: boolean,
  dirty: boolean,
  save: () => Promise<void>,
  delay = 1200,
  changeToken?: unknown,
) {
  const saveRef = useRef(save)
  saveRef.current = save
  useEffect(() => {
    if (!enabled || !dirty) return
    const timer = window.setTimeout(() => void saveRef.current(), delay)
    return () => window.clearTimeout(timer)
  }, [changeToken, delay, dirty, enabled])
}

export function useAsync<T>(loader: () => Promise<T>, dependencies: readonly unknown[]) {
  const [data, setData] = useState<T>()
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(true)
  const refresh = useCallback(async () => {
    setLoading(true)
    setError(undefined)
    try {
      setData(await loader())
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The request failed')
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)
  useEffect(() => void refresh(), [refresh])
  return { data, error, loading, refresh, setData }
}
